import {makeAutoObservable, runInAction} from "mobx";
import {AppService} from "../service/app.service";
import {AuthStore} from "./auth.store";
import {AccessStore} from "./access.store";
import {AccessService} from "../service/access.service";
import {AuthService} from "../service/auth.service";
import {ModuleStore} from "./module.store";
import {TranslationService} from "../service/module/translation.service";
import {ResponseTypeEnum, TranslationDomainEnum} from "../types/enums";
import {Translation} from "../types/modules";
import {TranslationListResponse} from "../types/response";
import {TranslationEditRequest} from "../types/request";

export class AppStore {
    public authStore: AuthStore;

    public accessStore: AccessStore;

    public moduleStore: ModuleStore;

    private _locale: string = 'en';

    private _breadcrumbs: Array<any> = [];

    private _countries: Array<any> = [];

    private _translations: { [key in TranslationDomainEnum]: { [key: string]: string } } = {
        [TranslationDomainEnum.MESSAGES]: {},
        [TranslationDomainEnum.LEXIK_TRANSLATION_BUNDLE]: {},
        [TranslationDomainEnum.SECURITY]: {},
        [TranslationDomainEnum.VALIDATORS]: {},
        [TranslationDomainEnum.USER_ADMINISTRATION]: {},
        [TranslationDomainEnum.COMPANY_ADMINISTRATION]: {},
    };

    private _translatorMode: boolean = false;

    private _activeTranslatorModeTranslation: Translation|null = null;

    private _activeTranslatorModeTranslationLoading: boolean = false;

    private _lastNavigationTime: number = Date.now();

    private _inactive: boolean = false;

    private _loading: boolean = false;

    private _responseMessage: string|null = null;

    private _responseMessageDomain: TranslationDomainEnum = TranslationDomainEnum.MESSAGES;

    private _responseType: ResponseTypeEnum | null = null;

    constructor(
        private readonly appService: AppService,
        private readonly translationService: TranslationService
    ) {
        makeAutoObservable(this);

        this.authStore = new AuthStore(this, new AuthService());
        this.accessStore = new AccessStore(this, new AccessService());
        this.moduleStore = new ModuleStore(this);
    }

    get locale(): string {
        return this._locale;
    }

    set locale(value: string) {
        this._locale = value;
    }

    get breadcrumbs(): Array<any> {
        return this._breadcrumbs;
    }

    setBreadcrumbs = (breadcrumbs: Array<any>) => {
        this._breadcrumbs = breadcrumbs;
    }

    get lastNavigationTime(): number {
        return this._lastNavigationTime;
    }

    setLastNavigationTime = (date: number) => {
        this._lastNavigationTime = date;
    }

    get inactive(): boolean {
        return this._inactive;
    }

    setInactive = (inactive: boolean) => {
        this._inactive = inactive;
    }

    get translations() {
        return this._translations;
    }

    set translations(value) {
        this._translations = value;
    }

    get translatorMode(): boolean {
        return this._translatorMode;
    }

    set translatorMode(value: boolean) {
        this._translatorMode = value;
    }

    get countries () {
        return this._countries;
    }

    set countries (countries) {
        this._countries = countries;
    }

    setTranslatorMode = (value: boolean) => {
        this.translatorMode = value;

        if (!value) {
            this.activeTranslatorModeTranslation = null;
        }
    }

    public toggleTranslatorMode = () => {
        this.setTranslatorMode(!this.translatorMode);
    }

    get activeTranslatorModeTranslation() {
        return this._activeTranslatorModeTranslation;
    }

    set activeTranslatorModeTranslation(value) {
        this._activeTranslatorModeTranslation = value;
    }

    public setActiveTranslatorModeTranslation = (value: Translation|null) => {
        this.activeTranslatorModeTranslation = value;
    }

    get activeTranslatorModeTranslationLoading() {
        return this._activeTranslatorModeTranslationLoading;
    }

    set activeTranslatorModeTranslationLoading(value) {
        this._activeTranslatorModeTranslationLoading = value;
    }

    get responseType() {
        return this._responseType;
    }

    set responseType(value) {
        this._responseType = value;
    }
    get responseMessage() {
        return this._responseMessage;
    }

    set responseMessage(value) {
        this._responseMessage = value;
    }

    set responseMessageDomain(value: TranslationDomainEnum) {
        this._responseMessageDomain = value;
    }

    get responseMessageDomain () {
        return this._responseMessageDomain;
    }

    get loading (): boolean{
        return this._loading;
    }

    set loading (loading: boolean) {
        this._loading = loading;
    }

    setLoading = (loading: boolean) => {
        this.loading = loading;
    }

    public setResponseMessage = (message: any, type: ResponseTypeEnum, domain: TranslationDomainEnum = TranslationDomainEnum.MESSAGES) => {
        this.responseMessage = message;
        this.responseMessageDomain = domain;
        this.responseType = type;
    }

    public resetResponseMessageType = () => {
        this.responseMessage = null;
        this.responseType = null;
    }

    public setLocale = (locale: string) => {
        localStorage.setItem('locale', locale);
        this.locale = locale;
    }

    public getLocales = async () => {
        return await this.translationService.listApplicationLocales();
    }

    public getCountries = async () => {
        return await this.appService.listApplicationCountries().then(({ data }) => {
            runInAction(() => {
                this.countries = data;
            })
        })
    }

    public findAddress = async (request: any) => {
        return await this.appService.findAddress(request);
    }

    public saveTranslation = async (request: TranslationEditRequest) => {
        return await this.translationService.edit(request);
    }

    public findTranslationForEditMode = (domain: string, message: string) => {
        this.activeTranslatorModeTranslationLoading = true;

        this.translationService.list({
            page: 1,
            rows: 1,
            searchFields: [{ field: '_domain', value: domain}, { field: '_key', value: message }]
        }).then(({ data }: {data: TranslationListResponse}) => {
            if (data.translations.length > 0) {
                this.activeTranslatorModeTranslation = data.translations[0];
                this.activeTranslatorModeTranslationLoading = false;
            }
        })
    }

    public invalidateTranslationCache = async () => {
        this.setLoading(true);
        return await this.translationService.invalidateCache().then(() => {
            this.setResponseMessage('global.message.translations_invalidated', ResponseTypeEnum.SUCCESS)
        }).finally(() => {
            this.setLoading(false);
        });
    }

    public updateApplicationTranslations = async () => {
        return await this.translationService.listApplicationTranslations()
            .then((response) => {
                this.translations = response.data;
            })
    }

    public translate = (message: string, params = {}, domain = TranslationDomainEnum.MESSAGES): string => {
        const replaceParamsInSentence = (sentence: string, params: { [key: string]: string }) => {
            return sentence.replace(/{{\s*([^}]+)\s*}}/g, (match, key) => {
                if (params.hasOwnProperty(key.trim())) {
                    return params[key.trim()];
                }
                return match;
            });
        };

        if (!this.translations[domain]?.hasOwnProperty(message)) {
            return replaceParamsInSentence(message, params);
        }

        return replaceParamsInSentence(this.translations[domain][message], params)
    }

    public fetchMenu = async () => {
        return await this.appService.fetchMenu();
    }
}